/**
 * Semantic Types Registry
 * 
 * Central registry of all semantic types in the system.
 * When adding a new type:
 * 1. Add it to SEMANTIC_TYPES below
 * 2. Create a detector in this folder (e.g., MyTypeDetector.ts)
 * 3. Register the detector in index.ts
 * 
 * @see ./index.ts for detector registration
 */

import type { ISemanticType } from '@/interfaces/ISemanticType';

/**
 * All semantic types supported by the system.
 * Single source of truth for type definitions, display info, and MIME types.
 */
export const SEMANTIC_TYPES = {
  // Structured data formats
  JSON: { 
    semanticType: 'JSON', 
    mimeType: 'application/json',
    description: 'JavaScript Object Notation',
    color: 'yellow'
  },
  XML: { 
    semanticType: 'XML', 
    mimeType: 'application/xml',
    description: 'Extensible Markup Language',
    color: 'orange'
  },
  YAML: { 
    semanticType: 'YAML', 
    mimeType: 'application/x-yaml',
    description: 'YAML Ain\'t Markup Language',
    color: 'purple'
  },
  TOML: {
    semanticType: 'TOML',
    mimeType: 'application/toml',
    description: 'Tom\'s Obvious, Minimal Language',
    color: 'violet'
  },
  CSV: {
    semanticType: 'CSV',
    mimeType: 'text/csv',
    description: 'Comma-Separated Values',
    color: 'emerald',
  },
  TSV: {
    semanticType: 'TSV',
    mimeType: 'text/tab-separated-values',
    description: 'Tab-Separated Values',
    color: 'teal',
  },
  PIPE: {
    semanticType: 'Pipe',
    mimeType: 'text/plain',
    description: 'Pipe-Separated Values',
    color: 'cyan',
  },
  HTML: { 
    semanticType: 'HTML', 
    mimeType: 'text/html',
    description: 'HyperText Markup Language',
    color: 'orange'
  },
  
  // Text formats
  TEXT: { 
    semanticType: 'Text', 
    mimeType: 'text/plain',
    description: 'Plain text',
    color: 'gray'
  },
  MARKDOWN: { 
    semanticType: 'Markdown', 
    mimeType: 'text/markdown',
    description: 'Markdown formatted text',
    color: 'slate'
  },

  // Patterns
  REGEX: {
    semanticType: 'Regex',
    description: 'Regular expression (JavaScript)',
    color: 'pink',
  },
  
  // Encoded formats
  JWT: { 
    semanticType: 'JWT',
    description: 'JSON Web Token',
    color: 'red'
  },
  BASE64: { 
    semanticType: 'Base64',
    description: 'Base64 encoded string',
    color: 'blue'
  },
  URL_ENCODED: { 
    semanticType: 'URLEncoded',
    description: 'URL-encoded string',
    color: 'cyan'
  },
  HEX: { 
    semanticType: 'Hex',
    description: 'Hexadecimal data',
    color: 'amber'
  },
  
  // Identifiers
  URL: { 
    semanticType: 'URL',
    description: 'Uniform Resource Locator',
    color: 'cyan'
  },
  UUID: { 
    semanticType: 'UUID',
    description: 'Universally Unique Identifier',
    color: 'green'
  },
  
  // Other
  HASH: { 
    semanticType: 'Hash',
    description: 'Cryptographic hash',
    color: 'pink'
  },
  SVG: { 
    semanticType: 'SVG', 
    mimeType: 'image/svg+xml',
    description: 'Scalable Vector Graphics',
    color: 'indigo'
  },
  TIMESTAMP: { 
    semanticType: 'Timestamp',
    description: 'Date/time value',
    color: 'teal'
  },
} as const satisfies Record<string, ISemanticType>;

/**
 * Type for semantic type keys (for type-safe lookups).
 */
export type SemanticTypeKey = keyof typeof SEMANTIC_TYPES;

/**
 * Helper to get semantic type info by semanticType string value.
 */
export function getSemanticTypeInfo(semanticType: string): ISemanticType | undefined {
  return Object.values(SEMANTIC_TYPES).find(t => t.semanticType === semanticType);
}
